import { useCallback, useEffect, useMemo, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { WEEKDAYS, toDateInputValue } from "../../../utils/appointmentLabels";
import {
  HubListGroup,
  HubRow,
  HubSheet,
} from "../AdminPortal/HubPwa";
import AdminDoctorSlotPreview from "./AdminDoctorSlotPreview";
import {
  filterAssignedBranches,
  pickDefaultBranchId,
  doctorBranchOptionsFromResponse,
} from "./doctorConstants";
import aa from "../AdminPortal/adminApp.module.css";
import { useToast } from "../../ui";

const DEFAULT_SCHEDULE = [1, 2, 3, 4, 5].map((day) => ({
  day_of_week: day,
  start_time: "09:00",
  end_time: "18:00",
  week_parity: "",
}));

const PARITY_OPTIONS = [
  { value: "", label: "Все недели" },
  { value: "0", label: "Чётные" },
  { value: "1", label: "Нечётные" },
];

function addMonths(dateStr, months) {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setMonth(d.getMonth() + months);
  return toDateInputValue(d);
}

function normalizeDate(raw) {
  if (!raw) return "";
  if (typeof raw === "string") return raw.slice(0, 10);
  return new Date(raw).toISOString().slice(0, 10);
}

function nextDay(iso) {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + 1);
  return toDateInputValue(d);
}

export default function TrainerSchedulePwa({
  doctorId,
  doctorBranchOptions,
  onScheduleSaved,
}) {
  const toast = useToast();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [scheduleForm, setScheduleForm] = useState(DEFAULT_SCHEDULE);
  const [overrides, setOverrides] = useState([]);
  const [activeDay, setActiveDay] = useState(1);
  const [saving, setSaving] = useState(false);
  const [exceptionOpen, setExceptionOpen] = useState(false);
  const [vacationOpen, setVacationOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [allDoctors, setAllDoctors] = useState([]);
  const [dupTargetBranch, setDupTargetBranch] = useState("");
  const [dupTargetDoctor, setDupTargetDoctor] = useState("");
  const [vacationFrom, setVacationFrom] = useState("");
  const [vacationTo, setVacationTo] = useState("");
  const [closedOverrides, setClosedOverrides] = useState([]);
  const [closedLoading, setClosedLoading] = useState(false);
  const [overrideDate, setOverrideDate] = useState(toDateInputValue());
  const [overrideKind, setOverrideKind] = useState("extra");
  const [overrideStart, setOverrideStart] = useState("10:00");
  const [overrideEnd, setOverrideEnd] = useState("14:00");

  useEffect(() => {
    const applyBranches = (options) => {
      const list = filterAssignedBranches(options);
      setBranches(list);
      setBranchId((prev) =>
        list.some((b) => b.id === prev) ? prev : pickDefaultBranchId(list),
      );
    };

    if (doctorBranchOptions?.length) {
      applyBranches(doctorBranchOptions);
      return;
    }
    if (!doctorId) return;

    clinicAPI
      .getDoctor(doctorId)
      .then((r) => applyBranches(doctorBranchOptionsFromResponse(r.doctor)))
      .catch(() => toast.error("Не удалось загрузить залы"));
  }, [doctorId, doctorBranchOptions, toast]);

  useEffect(() => {
    clinicAPI.getDoctors({ include_summary: "false" }).then((r) => {
      setAllDoctors(r.doctors || []);
    });
  }, []);

  const loadClosedOverrides = useCallback(() => {
    if (!doctorId || !branchId) return;
    const today = toDateInputValue();
    setClosedLoading(true);
    clinicAPI
      .getDoctorClosedOverrides(doctorId, {
        branch_id: branchId,
        from: today,
        to: addMonths(today, 6),
      })
      .then((r) => setClosedOverrides(r.overrides || []))
      .catch(() => setClosedOverrides([]))
      .finally(() => setClosedLoading(false));
  }, [doctorId, branchId]);

  useEffect(() => {
    if (!doctorId || !branchId) return;
    clinicAPI
      .getSchedule(doctorId, branchId)
      .then((r) => {
        const list = r.schedules || [];
        setOverrides(r.overrides || []);
        if (list.length) {
          setScheduleForm(
            list.map((x) => ({
              day_of_week: x.day_of_week,
              start_time: String(x.start_time).slice(0, 5),
              end_time: String(x.end_time).slice(0, 5),
              week_parity: x.week_parity != null ? String(x.week_parity) : "",
            })),
          );
        } else {
          setScheduleForm(DEFAULT_SCHEDULE);
        }
      })
      .catch(() => toast.error("Не удалось загрузить расписание"));
  }, [doctorId, branchId, toast]);

  const dayIntervals = useMemo(
    () =>
      scheduleForm
        .map((row, idx) => ({ row, idx }))
        .filter(({ row }) => row.day_of_week === activeDay),
    [scheduleForm, activeDay],
  );

  const daysWithSlots = useMemo(() => {
    const set = new Set(scheduleForm.map((r) => r.day_of_week));
    return set;
  }, [scheduleForm]);

  const branchName = branches.find((b) => b.id === branchId)?.name || "зал";

  const updateRow = (idx, patch) => {
    setScheduleForm((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const removeRow = (idx) => {
    setScheduleForm((prev) => prev.filter((_, i) => i !== idx));
  };

  const addInterval = () => {
    setScheduleForm((prev) => [
      ...prev,
      {
        day_of_week: activeDay,
        start_time: "09:00",
        end_time: "18:00",
        week_parity: "",
      },
    ]);
  };

  const saveWeekly = async () => {
    if (!branchId) {
      toast.error("Выберите зал");
      return;
    }
    setSaving(true);
    try {
      await clinicAPI.setSchedule(doctorId, branchId, scheduleForm);
      toast.success("Расписание сохранено");
      onScheduleSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const addOverride = async () => {
    if (!branchId) return;
    try {
      await clinicAPI.addScheduleOverride(doctorId, {
        branch_id: branchId,
        date: overrideDate,
        kind: overrideKind,
        start_time: overrideKind === "extra" ? overrideStart : null,
        end_time: overrideKind === "extra" ? overrideEnd : null,
      });
      const r = await clinicAPI.getSchedule(doctorId, branchId);
      setOverrides(r.overrides || []);
      setExceptionOpen(false);
      toast.success("Исключение добавлено");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const removeOverride = async (id) => {
    await clinicAPI.deleteScheduleOverride(doctorId, id);
    const r = await clinicAPI.getSchedule(doctorId, branchId);
    setOverrides(r.overrides || []);
    loadClosedOverrides();
    toast.success("Удалено");
  };

  const blockVacation = async () => {
    if (!vacationFrom || !vacationTo || !branchId) {
      toast.error("Укажите период");
      return;
    }
    if (vacationFrom > vacationTo) {
      toast.error("Дата «с» не может быть позже «по»");
      return;
    }
    try {
      const r = await clinicAPI.blockDoctorPeriod(doctorId, {
        from: vacationFrom,
        to: vacationTo,
        branch_id: branchId,
        note: "Отпуск",
      });
      toast.success(r.message || "Период закрыт");
      setVacationOpen(false);
      loadClosedOverrides();
      const sched = await clinicAPI.getSchedule(doctorId, branchId);
      setOverrides(sched.overrides || []);
      onScheduleSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const removeClosedBulk = async (from, to) => {
    try {
      const r = await clinicAPI.deleteDoctorClosedBulk(doctorId, {
        from,
        to,
        branch_id: branchId,
      });
      toast.success(r.message || "Период снят");
      loadClosedOverrides();
      const sched = await clinicAPI.getSchedule(doctorId, branchId);
      setOverrides(sched.overrides || []);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const duplicateSchedule = async () => {
    const targetBranch = dupTargetBranch || branchId;
    const targetDoctor = dupTargetDoctor || doctorId;
    const targetBranchName =
      branches.find((b) => b.id === targetBranch)?.name || "";
    const targetDoctorName =
      targetDoctor === doctorId
        ? "этот тренер"
        : allDoctors.find((d) => d.id === targetDoctor)?.full_name || "тренер";

    if (targetBranch === branchId && targetDoctor === doctorId) {
      toast.error("Выберите другой зал или другого тренера");
      return;
    }

    try {
      await clinicAPI.duplicateDoctorSchedule(doctorId, {
        source_branch_id: branchId,
        target_branch_id: targetBranch,
        target_doctor_id: targetDoctor,
      });
      toast.success(`Шаблон скопирован: ${targetDoctorName} · ${targetBranchName}`);
      setCopyOpen(false);
      onScheduleSaved?.(targetDoctor);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const closedGroups = useMemo(() => {
    const groups = [];
    let cur = null;
    for (const o of closedOverrides) {
      const d = normalizeDate(o.date);
      const prev = cur?.dates?.[cur.dates.length - 1];
      if (
        cur &&
        prev &&
        nextDay(prev) === d &&
        cur.note === (o.note || "Отпуск / блокировка")
      ) {
        cur.dates.push(d);
        cur.ids.push(o.id);
      } else {
        cur = {
          note: o.note || "Отпуск",
          dates: [d],
          ids: [o.id],
        };
        groups.push(cur);
      }
    }
    return groups;
  }, [closedOverrides]);

  useEffect(() => {
    if (vacationOpen) loadClosedOverrides();
  }, [vacationOpen, loadClosedOverrides]);

  if (!doctorId) return null;

  if (!branches.length) {
    return (
      <div className={aa.trainerScheduleEmpty}>
        <p className={aa.trainerScheduleEmptyTitle}>Залы не назначены</p>
        <p className={aa.hubHint}>
          Сначала привяжите тренера к залу во вкладке «Назначения».
        </p>
      </div>
    );
  }

  const activeDayLabel = WEEKDAYS.find((d) => d.id === activeDay)?.label || "";

  return (
    <div className={aa.trainerSchedule}>
      <div className={aa.trainerScheduleBranchBar}>
        <span className={aa.trainerScheduleBranchLabel}>Зал</span>
        <div className={aa.trainerScheduleBranchChips}>
          {branches.map((b) => (
            <button
              key={b.id}
              type="button"
              className={
                branchId === b.id
                  ? aa.trainerScheduleBranchChipOn
                  : aa.trainerScheduleBranchChip
              }
              onClick={() => setBranchId(b.id)}
            >
              {b.name}
            </button>
          ))}
        </div>
      </div>

      <p className={aa.trainerScheduleIntro}>
        Шаблон для «{branchName}». Несколько интервалов в один день — перерыв
        (например 9–12 и 14–18).
      </p>

      <div className={aa.trainerScheduleDayStrip}>
        {WEEKDAYS.map((day) => {
          const on = activeDay === day.id;
          const has = daysWithSlots.has(day.id);
          return (
            <button
              key={day.id}
              type="button"
              className={`${aa.trainerScheduleDayBtn} ${on ? aa.trainerScheduleDayBtnOn : ""}`}
              onClick={() => setActiveDay(day.id)}
            >
              <span>{day.short}</span>
              {has ? <i className={aa.trainerScheduleDayDot} aria-hidden /> : null}
            </button>
          );
        })}
      </div>

      <section className={aa.trainerScheduleDayPanel}>
        <header className={aa.trainerScheduleDayHead}>
          <h3 className={aa.trainerScheduleDayTitle}>{activeDayLabel}</h3>
          <button
            type="button"
            className={aa.trainerScheduleAddBtn}
            onClick={addInterval}
          >
            + Интервал
          </button>
        </header>

        {dayIntervals.length === 0 ? (
          <div className={aa.trainerScheduleDayEmpty}>
            <p>Нет рабочих часов</p>
            <button type="button" className={aa.hubSecondaryCta} onClick={addInterval}>
              Добавить интервал
            </button>
          </div>
        ) : (
          <div className={aa.trainerScheduleIntervals}>
            {dayIntervals.map(({ row, idx }) => (
              <article key={idx} className={aa.trainerScheduleInterval}>
                <div className={aa.trainerScheduleTimes}>
                  <input
                    type="time"
                    className={aa.trainerScheduleTimeInput}
                    value={row.start_time}
                    onChange={(e) => updateRow(idx, { start_time: e.target.value })}
                  />
                  <span className={aa.trainerScheduleTimeDash}>—</span>
                  <input
                    type="time"
                    className={aa.trainerScheduleTimeInput}
                    value={row.end_time}
                    onChange={(e) => updateRow(idx, { end_time: e.target.value })}
                  />
                  <button
                    type="button"
                    className={aa.trainerScheduleRemoveBtn}
                    aria-label="Удалить интервал"
                    onClick={() => removeRow(idx)}
                  >
                    ✕
                  </button>
                </div>
                <div className={aa.trainerScheduleParityRow}>
                  {PARITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={
                        String(row.week_parity ?? "") === opt.value
                          ? aa.trainerScheduleParityOn
                          : aa.trainerScheduleParity
                      }
                      onClick={() => updateRow(idx, { week_parity: opt.value })}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <button
        type="button"
        className={aa.hubPrimaryCta}
        onClick={saveWeekly}
        disabled={saving || !branchId}
      >
        {saving ? "Сохранение…" : "Сохранить шаблон"}
      </button>

      <div className={aa.trainerScheduleTools}>
        <button
          type="button"
          className={aa.trainerScheduleToolBtn}
          onClick={() => setVacationOpen(true)}
        >
          Отпуск
        </button>
        <button
          type="button"
          className={aa.trainerScheduleToolBtn}
          onClick={() => {
            setDupTargetBranch("");
            setDupTargetDoctor("");
            setCopyOpen(true);
          }}
        >
          Копировать
        </button>
      </div>

      <section className={aa.trainerScheduleSection}>
        <div className={aa.trainerScheduleSectionHead}>
          <h3 className={aa.hubSectionLabel}>Исключения</h3>
          <button
            type="button"
            className={aa.trainerScheduleLinkBtn}
            onClick={() => setExceptionOpen(true)}
          >
            + Добавить
          </button>
        </div>
        {overrides.length === 0 ? (
          <p className={aa.trainerScheduleMuted}>Выходные и доп. часы на дату</p>
        ) : (
          <HubListGroup>
            {overrides.slice(0, 10).map((o) => (
              <HubRow
                key={o.id}
                title={new Date(`${o.date}T12:00:00`).toLocaleDateString("ru-RU", {
                  day: "numeric",
                  month: "long",
                  weekday: "short",
                })}
                meta={
                  o.kind === "closed"
                    ? "Выходной"
                    : `${String(o.start_time).slice(0, 5)} – ${String(o.end_time).slice(0, 5)}`
                }
                badge={o.kind === "closed" ? "Закрыто" : "Доп."}
                badgeTone={o.kind === "closed" ? "danger" : "accent"}
                onClick={() => {
                  if (window.confirm("Удалить исключение?")) removeOverride(o.id);
                }}
              />
            ))}
          </HubListGroup>
        )}
      </section>

      <details className={aa.trainerScheduleDetails}>
        <summary>Проверить слоты для клиентов</summary>
        <div className={aa.trainerScheduleDetailsBody}>
          <AdminDoctorSlotPreview
            doctorId={doctorId}
            branchId={branchId}
            branches={branches}
          />
        </div>
      </details>

      <HubSheet
        open={exceptionOpen}
        onClose={() => setExceptionOpen(false)}
        title="Исключение на дату"
        subtitle={branchName}
      >
        <div className={aa.trainerExceptionSheet}>
          <label className={aa.trainerExceptionField}>
            <span>Дата</span>
            <input
              type="date"
              className={aa.trainerScheduleTimeInput}
              value={overrideDate}
              onChange={(e) => setOverrideDate(e.target.value)}
            />
          </label>

          <div className={aa.trainerExceptionKind}>
            <button
              type="button"
              className={
                overrideKind === "extra"
                  ? aa.trainerScheduleParityOn
                  : aa.trainerScheduleParity
              }
              onClick={() => setOverrideKind("extra")}
            >
              Доп. часы
            </button>
            <button
              type="button"
              className={
                overrideKind === "closed"
                  ? aa.trainerScheduleParityOn
                  : aa.trainerScheduleParity
              }
              onClick={() => setOverrideKind("closed")}
            >
              Выходной
            </button>
          </div>

          {overrideKind === "extra" ? (
            <div className={aa.trainerScheduleTimes}>
              <input
                type="time"
                className={aa.trainerScheduleTimeInput}
                value={overrideStart}
                onChange={(e) => setOverrideStart(e.target.value)}
              />
              <span className={aa.trainerScheduleTimeDash}>—</span>
              <input
                type="time"
                className={aa.trainerScheduleTimeInput}
                value={overrideEnd}
                onChange={(e) => setOverrideEnd(e.target.value)}
              />
            </div>
          ) : null}

          <button type="button" className={aa.hubPrimaryCta} onClick={addOverride}>
            Добавить
          </button>
        </div>
      </HubSheet>

      <HubSheet
        open={vacationOpen}
        onClose={() => setVacationOpen(false)}
        title="Отпуск на период"
        subtitle={branchName}
      >
        <div className={aa.trainerExceptionSheet}>
          <p className={aa.hubHint}>
            Закрывает каждый день в диапазоне. Для одного дня — «Исключения».
          </p>
          <label className={aa.trainerExceptionField}>
            <span>С</span>
            <input
              type="date"
              className={aa.trainerScheduleTimeInput}
              value={vacationFrom}
              onChange={(e) => setVacationFrom(e.target.value)}
            />
          </label>
          <label className={aa.trainerExceptionField}>
            <span>По</span>
            <input
              type="date"
              className={aa.trainerScheduleTimeInput}
              value={vacationTo}
              onChange={(e) => setVacationTo(e.target.value)}
            />
          </label>
          <button type="button" className={aa.hubPrimaryCta} onClick={blockVacation}>
            Закрыть период
          </button>

          {closedLoading ? (
            <p className={aa.hubHint}>Загрузка…</p>
          ) : closedGroups.length > 0 ? (
            <HubListGroup>
              {closedGroups.map((g) => {
                const from = g.dates[0];
                const to = g.dates[g.dates.length - 1];
                const label =
                  from === to
                    ? new Date(`${from}T12:00:00`).toLocaleDateString("ru-RU", {
                        day: "numeric",
                        month: "short",
                      })
                    : `${new Date(`${from}T12:00:00`).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })} — ${new Date(`${to}T12:00:00`).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })}`;
                return (
                  <HubRow
                    key={`${from}-${to}`}
                    title={label}
                    meta={`${g.dates.length} дн.`}
                    sub={g.note}
                    onClick={() => {
                      if (g.dates.length === 1) {
                        clinicAPI
                          .deleteScheduleOverride(doctorId, g.ids[0])
                          .then(() => {
                            loadClosedOverrides();
                            toast.success("День открыт");
                          });
                      } else if (
                        window.confirm(`Снять блокировку с ${label}?`)
                      ) {
                        removeClosedBulk(from, to);
                      }
                    }}
                  />
                );
              })}
            </HubListGroup>
          ) : null}
        </div>
      </HubSheet>

      <HubSheet
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        title="Копировать шаблон"
        subtitle={`Из «${branchName}»`}
      >
        <div className={aa.trainerExceptionSheet}>
          <p className={aa.hubHint}>
            Переносит текущий недельный шаблон. Шаблон у цели будет заменён.
          </p>
          <label className={aa.trainerExceptionField}>
            <span>Зал назначения</span>
            <select
              className={aa.trainerScheduleTimeInput}
              value={dupTargetBranch || branchId}
              onChange={(e) => setDupTargetBranch(e.target.value)}
            >
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                  {b.id === branchId ? " (текущий)" : ""}
                </option>
              ))}
            </select>
          </label>
          <label className={aa.trainerExceptionField}>
            <span>Тренер назначения</span>
            <select
              className={aa.trainerScheduleTimeInput}
              value={dupTargetDoctor || doctorId}
              onChange={(e) => setDupTargetDoctor(e.target.value)}
            >
              <option value={doctorId}>Этот тренер</option>
              {allDoctors
                .filter((d) => d.id !== doctorId)
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.full_name}
                  </option>
                ))}
            </select>
          </label>
          <button type="button" className={aa.hubPrimaryCta} onClick={duplicateSchedule}>
            Копировать шаблон
          </button>
        </div>
      </HubSheet>
    </div>
  );
}
